/* eslint-disable no-redeclare */
/* eslint-disable no-unused-vars */
/* eslint-disable react/jsx-curly-newline */

import React, { useEffect } from 'react';
import ReactModal from 'react-modal';
import { useQuery } from '@tanstack/react-query';
import { useFormik } from 'formik';
import * as yup from 'yup';
import {
  CustomCheckBox,
  RegularButton,
  ICommonDropdownOption,
  CommonDropdown,
  AlertAdapter,
  FullScreenLoading,
} from 'pg-ui-library';
import { IUser, ICompany } from 'src/interfaces';
import CompanyService from 'src/services/company.services';
import { useUpdateuserSupport } from '../../mutations/user-setting.mutation';
import {
  ModalContent,
  AllContent,
  Status1Label,
  Header,
  MainContent,
  ModuleContainer,
  CloseIconContainer,
  ModuleItem,
  CheckboxHeading,
  ModuleItemLabel,
  StatusContainer,
  StatusLabel,
  FormRow,
  FormRowHeading,
  CompanyGrid,
  CheckBoxContainer,
  Footer,
} from './styles';

export enum Application {
  ESS = 'ESS',
}

const Applicationoptions: ICommonDropdownOption[] = [
  { key: '0', label: 'Select', value: '' },
  { key: '2', label: Application.ESS, value: Application.ESS },
];

export const activeOptions: ICommonDropdownOption[] = [
  {
    key: 'false',
    label: 'Inactive',
    value: 'false',
  },
  {
    key: 'true',
    label: 'Active',
    value: 'true',
  },
];

export const SupportEditModal: React.FC<{
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  user: IUser;
  onSuccess: () => void;
}> = ({ isOpen, setIsOpen, user, onSuccess }) => {
  const updateUser = useUpdateuserSupport();

  const formik = useFormik({
    initialValues: {
      userId: user?.id,
      isActive: user?.isActive ? 'true' : 'false',
      permission: {
        benefits: user?.permissionsUser.benefits || false,
        documents: user?.permissionsUser.documents || false,
        tax_forms: user?.permissionsUser.tax_forms || false,
        pay_history: user?.permissionsUser.pay_history || false,
        hr_profile: user?.permissionsUser.hr_profile || false,
        admin: user?.permissionsUser.admin || false,
      },
      userCompanies: [] as string[],
      application: 'ESS',
    },
    validationSchema: yup.object().shape({
      permission: yup.object().shape({
        benefits: yup.boolean(),
        documents: yup.boolean(),
        tax_forms: yup.boolean(),
        pay_history: yup.boolean(),
        hr_profile: yup.boolean(),
        admin: yup.boolean(),
      }),
    }),
    onSubmit: async values => {
      const updatedValues = {
        ...values,
        isActive: values.isActive === 'true',
      };
      try {
        await updateUser.mutateAsync({ updateUser: updatedValues });
        AlertAdapter('Updated successfully', 'success');
        setIsOpen(false);
        if (onSuccess) onSuccess();
      } catch (error) {
        const errorMsg = error?.toString() || 'An error occurred while updating the user';
        AlertAdapter(errorMsg, 'error');
      }
    },
  });

  const { data } = useQuery({
    queryKey: ['users/companies'],
    queryFn: () => CompanyService.getCompanies(),
    enabled: true,
  });

  const { data: dataCompany, isLoading: companyLoading } = useQuery(
    ['users/companiesbyId', user.id],
    () => CompanyService.getCompaniesByUserID(user.id),
    {
      enabled: !!user.id,
    }
  );

  const companies = data?.data.items as ICompany[];

  useEffect(() => {
    if (dataCompany && dataCompany.data) {
      const userCompanies = dataCompany.data.map((company: ICompany) => company.id);
      formik.setValues({
        userId: user.id,
        isActive: user.isActive ? 'true' : 'false',
        permission: {
          benefits: user.permissionsUser.benefits || false,
          documents: user.permissionsUser.documents || false,
          tax_forms: user.permissionsUser.tax_forms || false,
          pay_history: user.permissionsUser.pay_history || false,
          hr_profile: user.permissionsUser.hr_profile || false,
          admin: user.permissionsUser.admin || false,
        },
        userCompanies,
        application: user.applications && user.applications.length > 0 ? user.applications[0] : '',
      });
    }
  }, [user, dataCompany]);

  return (
    <ReactModal
      isOpen={isOpen}
      ariaHideApp={false}
      onRequestClose={onSuccess}
      style={{
        overlay: {
          margin: 'auto',
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
        },
        content: {
          borderRadius: '30px',
          margin: 'auto',
          width: '760px',
          height: '600px',
          display: 'flex',
          flexDirection: 'column',
          padding: '0',
          background: '#F3F4F8',
          overflowY: 'hidden',
        },
      }}
    >
      {companyLoading ? (
        <FullScreenLoading />
      ) : (
        <ModalContent>
          {!companyLoading && (
            <>
              <Header>
                <h3>Edit user</h3>
                <CloseIconContainer onClick={() => onSuccess()}>&#x2715;</CloseIconContainer>
              </Header>
              <AllContent>
                <MainContent>
                  <ModuleContainer>
                    <ModuleItemLabel>Module</ModuleItemLabel>
                    <ModuleItem>
                      <CustomCheckBox
                        id="benefits"
                        label=""
                        name="permission.benefits"
                        checked={formik.values.permission.benefits}
                        onChange={() => formik.setFieldValue('permission.benefits', !formik.values.permission.benefits)}
                      />
                      <CheckboxHeading>Benefit</CheckboxHeading>
                    </ModuleItem>
                    <ModuleItem>
                      <CustomCheckBox
                        id="pay_history"
                        label=""
                        name="pay_history"
                        checked={formik.values.permission.pay_history}
                        onChange={() =>
                          formik.setFieldValue('permission.pay_history', !formik.values.permission.pay_history)
                        }
                      />
                      <CheckboxHeading>Payroll</CheckboxHeading>
                    </ModuleItem>
                    {/* <ModuleItem>
                      <CustomCheckBox
                        id="admin"
                        label=""
                        name="admin"
                        checked={formik.values.permission.admin}
                        onChange={() => formik.setFieldValue('permission.admin', !formik.values.permission.admin)}
                      />
                      <CheckboxHeading>Admin</CheckboxHeading>
                    </ModuleItem> */}
                    <ModuleItem>
                      <CustomCheckBox
                        id="hr_profile"
                        label=""
                        name="hr_profile"
                        checked={formik.values.permission.hr_profile}
                        onChange={() =>
                          formik.setFieldValue('permission.hr_profile', !formik.values.permission.hr_profile)
                        }
                      />
                      <CheckboxHeading>Profile</CheckboxHeading>
                    </ModuleItem>
                    {/* <ModuleItem>
                      <CustomCheckBox
                        id="documents"
                        label=""
                        name="permission.documents"
                        checked={formik.values.permission.documents}
                        onChange={() =>
                          formik.setFieldValue('permission.documents', !formik.values.permission.documents)
                        }
                      />
                      <CheckboxHeading>Documents</CheckboxHeading>
                    </ModuleItem> */}
                    <ModuleItem>
                      <CustomCheckBox
                        label=""
                        id="tax_forms"
                        name="tax_forms"
                        checked={formik.values.permission.tax_forms}
                        onChange={() =>
                          formik.setFieldValue('permission.tax_forms', !formik.values.permission.tax_forms)
                        }
                      />
                      <CheckboxHeading>Tax Forms</CheckboxHeading>
                    </ModuleItem>
                  </ModuleContainer>
                  <StatusContainer>
                    <StatusLabel>Status</StatusLabel>
                    <div style={{ minWidth: '100%', marginTop: '10%' }}>
                      <CommonDropdown
                        label=""
                        id="isActive"
                        name="isActive"
                        options={activeOptions}
                        value={formik.values.isActive}
                        setValue={(value: string) => formik.setFieldValue('isActive', value)}
                        selectStyle={{
                          width: '100%',
                          marginTop: '8px',
                          height: 'auto',
                        }}
                      />
                    </div>

                    <Status1Label>Application</Status1Label>
                    <div style={{ minWidth: '100%', marginTop: '10%' }}>
                      <CommonDropdown
                        label=""
                        id="application"
                        name="application"
                        options={Applicationoptions}
                        value={formik.values.application}
                        setValue={(value: string) => formik.setFieldValue('application', value)}
                        selectStyle={{
                          width: '100%',
                          marginTop: '8px',
                          height: 'auto',
                        }}
                      />
                    </div>
                  </StatusContainer>
                </MainContent>
                <FormRowHeading>Company</FormRowHeading>
                <FormRow>
                  <CompanyGrid>
                    {companies.map(company => {
                      const isChecked = formik.values.userCompanies.includes(company.id);
                      return (
                        <CheckBoxContainer key={company.id}>
                          <CustomCheckBox
                            label=""
                            id={company.id}
                            name={company.legalEntityId}
                            checked={isChecked}
                            onChange={() => {
                              const selected = formik.values.userCompanies;
                              if (selected.includes(company.id)) {
                                formik.setFieldValue(
                                  'userCompanies',
                                  selected.filter(id => id !== company.id)
                                );
                              } else {
                                formik.setFieldValue('userCompanies', [...selected, company.id]);
                              }
                            }}
                          />
                          <span>{company.legalEntityId}</span>
                        </CheckBoxContainer>
                      );
                    })}
                  </CompanyGrid>
                </FormRow>
                <Footer>
                  <RegularButton
                    type="button"
                    onClick={() => {
                      onSuccess();
                      formik.resetForm();
                    }}
                    style={{ color: '#4948AC', backgroundColor: '#fff' }}
                  >
                    Cancel
                  </RegularButton>

                  <RegularButton
                    type="submit"
                    onClick={() => formik.handleSubmit()}
                    disabled={formik.isSubmitting || !formik.isValid}
                  >
                    Save
                  </RegularButton>
                </Footer>
              </AllContent>
            </>
          )}
        </ModalContent>
      )}
    </ReactModal>
  );
};
